<template>
  <div class="role-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>角色管理</span>
          <el-button type="primary" @click="handleCreate">新增角色</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="角色名称">
          <el-input
            v-model="queryParams.roleName"
            placeholder="请输入角色名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属企业" v-if="isSystemAdmin">
          <el-select v-model="queryParams.companyId" placeholder="全部" clearable style="width: 200px">
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 角色列表 -->
      <el-table
        v-loading="loading"
        :data="roleList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="roleCode" label="角色编码" width="130" />
        <el-table-column prop="roleName" label="角色名称" width="110" />
        <el-table-column prop="description" label="描述" min-width="180" show-overflow-tooltip />
        <el-table-column prop="companyName" label="所属企业" width="150" v-if="isSystemAdmin" />
        <el-table-column prop="sortOrder" label="排序" width="70" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="420" fixed="right">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
              <el-button size="small" @click="handleViewMenus(row)">
                <el-icon><View /></el-icon>
                查看菜单
              </el-button>
              <el-button size="small" @click="handleAssignMenus(row)">
                <el-icon><Setting /></el-icon>
                分配菜单
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.pageNum"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑角色对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="roleFormRef"
        :model="roleForm"
        :rules="roleFormRules"
        label-width="100px"
      >
        <el-form-item label="角色编码" prop="roleCode">
          <el-input v-model="roleForm.roleCode" />
        </el-form-item>
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="roleForm.roleName" />
        </el-form-item>
        <el-form-item label="所属企业" prop="companyId">
          <el-select
            v-model="roleForm.companyId"
            placeholder="请选择企业（可选）"
            clearable
            filterable
            style="width: 100%"
            :disabled="!isSystemAdmin"
          >
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
          <div v-if="!isSystemAdmin" style="color: #909399; font-size: 12px; margin-top: 4px;">
            非系统管理员创建的角色将自动关联到当前企业
          </div>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="roleForm.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="roleForm.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="roleForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 分配菜单对话框 -->
    <el-dialog
      v-model="menuDialogVisible"
      title="分配菜单"
      width="500px"
      @close="handleMenuDialogClose"
    >
      <el-tree
        ref="menuTreeRef"
        :data="dialogMenuTreeData"
        :props="{ label: 'menuName', children: 'children' }"
        show-checkbox
        node-key="id"
        :default-expand-all="true"
      />
      <template #footer>
        <el-button @click="menuDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAssignMenusSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 查看已绑定菜单对话框 -->
    <el-dialog
      v-model="viewMenuDialogVisible"
      title="已绑定菜单"
      width="500px"
    >
      <el-tree
        :data="viewMenuTreeData"
        :props="{ label: 'menuName', children: 'children' }"
        :default-expand-all="true"
      />
      <template #footer>
        <el-button type="primary" @click="viewMenuDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 查看已绑定权限对话框 -->
    <el-dialog
      v-model="viewPermissionDialogVisible"
      title="已绑定权限"
      width="700px"
    >
      <div v-if="viewPermissionList.length === 0" style="text-align: center; padding: 40px; color: #909399;">
        该角色暂未分配权限
      </div>
      <div v-else>
        <el-table :data="viewPermissionList" border style="width: 100%">
          <el-table-column prop="menuName" label="菜单名称" width="200" />
          <el-table-column prop="menuCode" label="菜单编码" width="150" />
          <el-table-column label="权限操作" align="center">
            <template #default="{ row }">
              <el-tag
                v-for="permission in row.permissions"
                :key="permission"
                :type="getPermissionTagType(permission)"
                size="small"
                style="margin-right: 5px;"
              >
                {{ getPermissionName(permission) }}
              </el-tag>
              <span v-if="row.permissions.length === 0" style="color: #909399;">无权限</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template #footer>
        <el-button type="primary" @click="viewPermissionDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 分配权限对话框 -->
    <el-dialog
      v-model="permissionDialogVisible"
      title="分配权限"
      width="600px"
      @close="handlePermissionDialogClose"
    >
      <div v-if="permissionMenuList.length === 0" style="text-align: center; padding: 40px; color: #909399;">
        请先为该角色分配菜单
      </div>
      <div v-else>
        <el-table :data="permissionMenuList" border style="width: 100%">
          <el-table-column prop="menuName" label="菜单名称" width="200" />
          <el-table-column label="权限操作" align="center">
            <template #default="{ row }">
              <div style="display: flex; align-items: center; gap: 10px;">
                <el-checkbox
                  :model-value="isAllPermissionsChecked(row)"
                  :indeterminate="isIndeterminate(row)"
                  @change="handleSelectAll(row, $event)"
                >
                  全选
                </el-checkbox>
                <el-divider direction="vertical" />
                <el-checkbox-group v-model="row.permissions">
                  <el-checkbox label="QUERY">查看</el-checkbox>
                  <el-checkbox label="ADD">新增</el-checkbox>
                  <el-checkbox label="EDIT">编辑</el-checkbox>
                  <el-checkbox label="DELETE">删除</el-checkbox>
                </el-checkbox-group>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template #footer>
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAssignPermissionsSubmit" :disabled="permissionMenuList.length === 0">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { View, Setting } from '@element-plus/icons-vue'
import {
  getRoleList,
  getRoleById,
  createRole,
  updateRole,
  assignMenus,
  assignPermissions
} from '../api/sysRole'
import { getMenuTree, getMenuTreeForRole } from '../api/sysMenu'
import { getAllPermissions, createPermission } from '../api/sysPermission'
import { getAllCompanies } from '../api/company'

// 数据
const loading = ref(false)
const roleList = ref([])
const total = ref(0)
const menuTreeData = ref([])
const originalMenuTreeData = ref([]) // 保存原始的完整菜单树
const dialogMenuTreeData = ref([]) // 对话框专用的菜单树数据
const permissionTreeData = ref([])
const originalPermissionTreeData = ref([]) // 保存原始的完整权限树
const allMenuList = ref([]) // 保存所有菜单的扁平列表，用于匹配权限
const companyList = ref([]) // 企业列表
const isSystemAdmin = ref(false) // 是否为系统管理员
const queryParams = reactive({
  roleName: '',
  status: null,
  companyId: null,
  pageNum: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const menuDialogVisible = ref(false)
const viewMenuDialogVisible = ref(false)
const viewPermissionDialogVisible = ref(false)
const permissionDialogVisible = ref(false)
const dialogTitle = ref('新增角色')
const isEdit = ref(false)
const roleFormRef = ref(null)
const menuTreeRef = ref(null)
const currentRoleId = ref(null)
const checkedMenuIds = ref([])
const viewMenuTreeData = ref([])
const viewPermissionList = ref([]) // 用于查看权限的列表
const permissionMenuList = ref([]) // 用于权限分配的菜单列表

const roleForm = reactive({
  id: null,
  roleCode: '',
  roleName: '',
  roleType: '',
  description: '',
  companyId: null,
  sortOrder: 0,
  status: 1
})

const roleFormRules = {
  roleCode: [{ required: true, message: '请输入角色编码', trigger: 'blur' }],
  roleName: [{ required: true, message: '请输入角色名称', trigger: 'blur' }]
}

// 方法
const loadRoleList = async () => {
  loading.value = true
  try {
    const res = await getRoleList(queryParams)
    if (res.code === '000000') {
      roleList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取角色列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 将菜单树扁平化
const flattenMenuTree = (menuTree, result = []) => {
  for (const menu of menuTree) {
    result.push(menu)
    if (menu.children && menu.children.length > 0) {
      flattenMenuTree(menu.children, result)
    }
  }
  return result
}

const loadMenuTree = async () => {
  try {
    const res = await getMenuTree(null)
    if (res.code === '000000') {
      const treeData = res.data || []
      menuTreeData.value = treeData
      originalMenuTreeData.value = JSON.parse(JSON.stringify(treeData)) // 深拷贝保存原始数据
      // 保存扁平化的菜单列表，用于匹配权限
      allMenuList.value = flattenMenuTree(JSON.parse(JSON.stringify(treeData)))
    }
  } catch (error) {
    console.error('获取菜单树失败:', error)
  }
}

const loadPermissionTree = async () => {
  try {
    const res = await getAllPermissions()
    if (res.code === '000000') {
      // 将权限列表转换为树形结构
      const treeData = buildPermissionTree(res.data || [])
      permissionTreeData.value = treeData
      originalPermissionTreeData.value = JSON.parse(JSON.stringify(treeData)) // 深拷贝保存原始数据
    }
  } catch (error) {
    console.error('获取权限列表失败:', error)
  }
}

const buildPermissionTree = (permissions) => {
  const map = new Map()
  const roots = []

  // 创建节点映射
  permissions.forEach(perm => {
    map.set(perm.id, { ...perm, children: [] })
  })

  // 构建树
  permissions.forEach(perm => {
    const node = map.get(perm.id)
    if (perm.parentId == null) {
      roots.push(node)
    } else {
      const parent = map.get(perm.parentId)
      if (parent) {
        parent.children.push(node)
      } else {
        roots.push(node)
      }
    }
  })

  return roots
}

/**
 * 根据企业ID过滤菜单树
 * 只保留该企业下的菜单（companyId匹配）和公共菜单（companyId为null）
 */
// eslint-disable-next-line no-unused-vars
const filterMenuTreeByCompanyId = (menuTree, companyId) => {
  if (!menuTree || menuTree.length === 0) {
    return []
  }
  
  const filtered = []
  for (const menu of menuTree) {
    // 检查当前菜单是否符合条件：企业ID匹配或为公共菜单（companyId为null）
    const menuCompanyId = menu.companyId
    const isMatch = menuCompanyId === null || menuCompanyId === companyId
    
    if (isMatch) {
      // 递归过滤子菜单
      const filteredChildren = menu.children && menu.children.length > 0
        ? filterMenuTreeByCompanyId(menu.children, companyId)
        : []
      
      // 创建新的菜单节点
      const filteredMenu = { ...menu }
      filteredMenu.children = filteredChildren
      filtered.push(filteredMenu)
    } else {
      // 如果当前菜单不匹配，但子菜单可能匹配，需要检查子菜单
      if (menu.children && menu.children.length > 0) {
        const filteredChildren = filterMenuTreeByCompanyId(menu.children, companyId)
        if (filteredChildren.length > 0) {
          // 如果子菜单有匹配的，保留当前菜单但只保留匹配的子菜单
          const filteredMenu = { ...menu }
          filteredMenu.children = filteredChildren
          filtered.push(filteredMenu)
        }
      }
    }
  }
  
  return filtered
}


/**
 * 根据角色类型过滤菜单树
 * 只保留 roleType 为 null 或等于指定角色类型的菜单
 */
const filterMenuTreeByRoleType = (menuTree, roleType) => {
  if (!menuTree || menuTree.length === 0) {
    return []
  }
  
  const filtered = []
  for (const menu of menuTree) {
    // 检查当前菜单是否符合条件
    const menuRoleType = menu.roleType
    const isMatch = !menuRoleType || menuRoleType === roleType
    
    if (isMatch) {
      // 递归过滤子菜单
      const filteredChildren = menu.children && menu.children.length > 0
        ? filterMenuTreeByRoleType(menu.children, roleType)
        : []
      
      // 创建新的菜单节点
      const filteredMenu = { ...menu }
      filteredMenu.children = filteredChildren
      filtered.push(filteredMenu)
    } else {
      // 如果当前菜单不匹配，但子菜单可能匹配，需要检查子菜单
      if (menu.children && menu.children.length > 0) {
        const filteredChildren = filterMenuTreeByRoleType(menu.children, roleType)
        if (filteredChildren.length > 0) {
          // 如果子菜单有匹配的，保留当前菜单但只保留匹配的子菜单
          const filteredMenu = { ...menu }
          filteredMenu.children = filteredChildren
          filtered.push(filteredMenu)
        }
      }
    }
  }
  
  return filtered
}

const handleSearch = () => {
  queryParams.pageNum = 1
  loadRoleList()
}

const handleReset = () => {
  queryParams.roleName = ''
  queryParams.status = null
  queryParams.companyId = null
  queryParams.pageNum = 1
  loadRoleList()
}

// 加载企业列表
const loadCompanyList = async () => {
  try {
    const res = await getAllCompanies()
    if (res.code === '000000') {
      companyList.value = res.data || []
    }
  } catch (error) {
    console.error('获取企业列表失败:', error)
  }
}

const handleCreate = () => {
  dialogTitle.value = '新增角色'
  isEdit.value = false
  Object.assign(roleForm, {
    id: null,
    roleCode: '',
    roleName: '',
    roleType: '',
    description: '',
    companyId: null,
    sortOrder: 0,
    status: 1
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑角色'
  isEdit.value = true
  try {
    const res = await getRoleById(row.id)
    if (res.code === '000000') {
      Object.assign(roleForm, res.data)
    }
    dialogVisible.value = true
  } catch (error) {
    console.error('获取角色详情失败:', error)
  }
}

const handleSubmit = async () => {
  if (!roleFormRef.value) return
  await roleFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = isEdit.value ? await updateRole(roleForm) : await createRole(roleForm)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadRoleList()
          // 发送全局事件，通知其他页面刷新角色列表
          window.dispatchEvent(new CustomEvent('role-updated', {
            detail: { 
              action: isEdit.value ? 'update' : 'create',
              roleId: res.data?.id || roleForm.id
            }
          }))
        }
      } catch (error) {
        console.error('保存角色失败:', error)
      }
    }
  })
}

// eslint-disable-next-line no-unused-vars
const handleAssignMenus = async (row) => {
  currentRoleId.value = row.id
  try {
    const res = await getRoleById(row.id)
    if (res.code === '000000') {
      const roleMenuIds = res.data.menuIds || []
      const roleType = res.data.roleType
      const roleCompanyId = res.data.companyId
      
      // 根据角色的企业ID获取菜单树（包含该企业的菜单和公共菜单，包括审批流配置管理菜单）
      // 无论是否为系统管理员，都根据角色的企业ID来获取菜单树
      console.log('分配菜单 - 角色信息:', {
        roleId: row.id,
        roleName: res.data.roleName,
        roleCompanyId: roleCompanyId,
        roleType: roleType
      })
      
      let menuTreeRes
      if (roleCompanyId != null) {
        // 如果角色有企业ID，获取该企业的菜单和公共菜单（companyId为null的菜单）
        // getMenuTreeForRole 应该已经返回该企业的菜单和公共菜单
        console.log('根据角色企业ID获取菜单树（包含公共菜单）:', roleCompanyId)
        menuTreeRes = await getMenuTreeForRole(null, roleCompanyId)
        console.log('获取到的菜单树数据:', menuTreeRes.data)
      } else {
        // 如果角色没有企业ID（公共角色），获取所有公共菜单（companyId为null的菜单）
        console.log('角色没有企业ID，获取公共菜单树')
        menuTreeRes = await getMenuTreeForRole(null, null)
        console.log('获取到的菜单树数据:', menuTreeRes.data)
      }
      
      if (menuTreeRes.code !== '000000') {
        ElMessage.error('获取菜单树失败: ' + (menuTreeRes.message || '未知错误'))
        return
      }
      
      // 准备对话框专用的菜单树数据
      let filteredMenuTree = menuTreeRes.data || []
      
      // 根据角色的企业ID过滤菜单树
      // 如果角色有企业ID，只保留：
      // 1. 公共菜单（companyId为null）
      // 2. 该企业的菜单（companyId等于角色的企业ID）
      // 如果角色没有企业ID，只保留：
      // 1. 公共菜单（companyId为null）
      if (roleCompanyId != null) {
        // 角色属于某个企业，只显示公共菜单和该企业的菜单
        filteredMenuTree = filterMenuTreeByCompanyId(filteredMenuTree, roleCompanyId)
      } else {
        // 角色不属于任何企业，只显示公共菜单
        filteredMenuTree = filterMenuTreeByCompanyId(filteredMenuTree, null)
      }
      
      console.log('过滤后的菜单树:', filteredMenuTree)
      
      // 根据角色类型过滤
      if (roleType) {
        filteredMenuTree = filterMenuTreeByRoleType(filteredMenuTree, roleType)
      }
      
      // 设置对话框菜单树数据
      dialogMenuTreeData.value = filteredMenuTree
      
      // 等待菜单树数据更新后，再打开对话框
      await nextTick()
      
      // 打开对话框
      menuDialogVisible.value = true
      
      // 等待对话框和树组件完全渲染后，再设置选中状态
      await nextTick()
      setTimeout(() => {
        if (menuTreeRef.value) {
          // 清除之前的选中状态
          menuTreeRef.value.setCheckedKeys([])
          // 设置当前角色的菜单选中状态
          // 需要过滤掉不在当前菜单树中的菜单ID（因为可能被角色类型过滤掉了）
          const filteredMenuIds = filterMenuIdsByTree(roleMenuIds, dialogMenuTreeData.value)
          checkedMenuIds.value = filteredMenuIds
          menuTreeRef.value.setCheckedKeys(filteredMenuIds)
        }
      }, 150)
    }
  } catch (error) {
    console.error('获取角色详情失败:', error)
  }
}

/**
 * 根据当前显示的菜单树过滤菜单ID列表
 * 只保留在当前菜单树中存在的菜单ID
 */
const filterMenuIdsByTree = (menuIds, menuTree) => {
  if (!menuIds || menuIds.length === 0) {
    return []
  }
  
  // 将菜单树扁平化，获取所有菜单ID
  const allTreeMenuIds = new Set()
  const collectMenuIds = (tree) => {
    tree.forEach(menu => {
      allTreeMenuIds.add(menu.id)
      if (menu.children && menu.children.length > 0) {
        collectMenuIds(menu.children)
      }
    })
  }
  collectMenuIds(menuTree)
  
  // 只返回在当前菜单树中存在的菜单ID
  return menuIds.filter(id => allTreeMenuIds.has(id))
}

const handleAssignMenusSubmit = async () => {
  if (!menuTreeRef.value) return
  // 获取完全选中的节点（不包括半选状态的父节点）
  const checkedKeys = menuTreeRef.value.getCheckedKeys(false)
  
  try {
    const res = await assignMenus(currentRoleId.value, checkedKeys)
    if (res.code === '000000') {
      ElMessage.success('分配菜单成功')
      menuDialogVisible.value = false
      // 保存成功后，更新 checkedMenuIds 以便下次打开时正确显示
      checkedMenuIds.value = checkedKeys
    }
  } catch (error) {
    console.error('分配菜单失败:', error)
    ElMessage.error('分配菜单失败：' + (error.response?.data?.message || error.message || '未知错误'))
  }
}

// 查看已绑定菜单
// eslint-disable-next-line no-unused-vars
const handleViewMenus = async (row) => {
  try {
    const res = await getRoleById(row.id)
    if (res.code === '000000') {
      const assignedMenuIds = res.data.menuIds || []
      
      if (assignedMenuIds.length === 0) {
        ElMessage.info('该角色暂未分配菜单')
        return
      }
      
      // 从完整的菜单树中筛选出已分配的菜单
      const allMenus = flattenMenuTree(JSON.parse(JSON.stringify(originalMenuTreeData.value)))
      const assignedMenus = allMenus.filter(menu => assignedMenuIds.includes(menu.id))
      
      // 构建菜单树（只显示已分配的菜单及其父级）
      viewMenuTreeData.value = buildMenuTreeFromIds(assignedMenus, originalMenuTreeData.value)
      viewMenuDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取角色详情失败:', error)
    ElMessage.error('获取角色详情失败')
  }
}

// 查看已绑定权限
// eslint-disable-next-line no-unused-vars
const handleViewPermissions = async (row) => {
  try {
    const res = await getRoleById(row.id)
    if (res.code === '000000') {
      const assignedMenuIds = res.data.menuIds || []
      // 确保 permissionIds 是数组
      let assignedPermissionIds = res.data.permissionIds || []
      if (!Array.isArray(assignedPermissionIds)) {
        assignedPermissionIds = []
      }
      
      console.log('查看权限 - 角色信息:', {
        roleId: row.id,
        roleName: res.data.roleName,
        assignedMenuIds,
        assignedPermissionIds,
        assignedPermissionIdsCount: assignedPermissionIds.length,
        permissionIdsType: typeof res.data.permissionIds
      })
      
      if (assignedMenuIds.length === 0) {
        ElMessage.info('该角色暂未分配菜单，无法查看权限')
        return
      }
      
      if (assignedPermissionIds.length === 0) {
        viewPermissionList.value = []
        viewPermissionDialogVisible.value = true
        return
      }
      
      // 获取所有权限列表
      const permissionsRes = await getAllPermissions()
      const allPermissions = permissionsRes.code === '000000' ? permissionsRes.data || [] : []
      
      // 获取已分配的菜单信息
      const allMenus = flattenMenuTree(JSON.parse(JSON.stringify(originalMenuTreeData.value)))
      const assignedMenus = allMenus.filter(menu => assignedMenuIds.includes(menu.id))
      
      // 构建权限列表
      const allPermissionTypes = ['QUERY', 'ADD', 'EDIT', 'DELETE']
      viewPermissionList.value = assignedMenus.map(menu => {
        // 查找该菜单对应的所有可能的权限
        const menuPermissions = allPermissions.filter(perm => {
          if (perm.permissionCode && menu.menuCode) {
            const permCode = perm.permissionCode.toUpperCase()
            const menuCode = menu.menuCode.toUpperCase()
            return permCode.startsWith(menuCode + '_')
          }
          return false
        })
        
        // 从已分配的权限ID中，找到匹配该菜单的权限
        const assignedMenuPermissions = menuPermissions.filter(perm => 
          assignedPermissionIds.includes(perm.id)
        )
        
        // 提取操作类型
        const menuPermissionIds = assignedMenuPermissions
          .map(perm => {
            const permCode = perm.permissionCode.toUpperCase()
            const menuCode = menu.menuCode.toUpperCase()
            if (permCode.startsWith(menuCode + '_')) {
              const operation = permCode.substring(menuCode.length + 1)
              if (allPermissionTypes.includes(operation)) {
                return operation
              }
            }
            return null
          })
          .filter(op => op !== null)
        
        return {
          menuId: menu.id,
          menuName: menu.menuName,
          menuCode: menu.menuCode,
          permissions: menuPermissionIds
        }
      })
      
      viewPermissionDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取角色详情失败:', error)
    ElMessage.error('获取角色详情失败')
  }
}

// 获取权限名称
const getPermissionName = (permission) => {
  const permissionMap = {
    'QUERY': '查看',
    'ADD': '新增',
    'EDIT': '编辑',
    'DELETE': '删除'
  }
  return permissionMap[permission] || permission
}

// 获取权限标签类型
const getPermissionTagType = (permission) => {
  const typeMap = {
    'QUERY': 'info',
    'ADD': 'success',
    'EDIT': 'warning',
    'DELETE': 'danger'
  }
  return typeMap[permission] || ''
}

/**
 * 根据菜单ID列表构建菜单树
 */
const buildMenuTreeFromIds = (assignedMenus, fullMenuTree) => {
  if (!assignedMenus || assignedMenus.length === 0) {
    return []
  }
  
  const menuMap = new Map()
  assignedMenus.forEach(menu => {
    menuMap.set(menu.id, { ...menu, children: [] })
  })
  
  // 需要包含父级菜单
  const includedIds = new Set(assignedMenus.map(m => m.id))
  const allMenus = flattenMenuTree(JSON.parse(JSON.stringify(fullMenuTree)))
  
  // 递归添加父级菜单
  const addParents = (menu) => {
    if (menu.parentId) {
      const parent = allMenus.find(m => m.id === menu.parentId)
      if (parent && !includedIds.has(parent.id)) {
        includedIds.add(parent.id)
        menuMap.set(parent.id, { ...parent, children: [] })
        addParents(parent)
      }
    }
  }
  
  assignedMenus.forEach(menu => addParents(menu))
  
  // 构建树结构
  const roots = []
  menuMap.forEach((menu) => {
    if (!menu.parentId || !menuMap.has(menu.parentId)) {
      roots.push(menu)
    } else {
      const parent = menuMap.get(menu.parentId)
      if (parent) {
        if (!parent.children) parent.children = []
        parent.children.push(menu)
      }
    }
  })
  
  return roots
}

// eslint-disable-next-line no-unused-vars
const handleAssignPermissions = async (row) => {
  currentRoleId.value = row.id
  try {
    const res = await getRoleById(row.id)
    if (res.code === '000000') {
      const assignedMenuIds = res.data.menuIds || []
      // 确保 permissionIds 是数组
      let assignedPermissionIds = res.data.permissionIds || []
      if (!Array.isArray(assignedPermissionIds)) {
        assignedPermissionIds = []
      }
      
      console.log('读取角色权限:', {
        roleId: row.id,
        roleName: res.data.roleName,
        assignedMenuIds,
        assignedPermissionIds,
        assignedPermissionIdsCount: assignedPermissionIds.length,
        permissionIdsType: typeof res.data.permissionIds
      })
      
      if (assignedMenuIds.length === 0) {
        permissionMenuList.value = []
        permissionDialogVisible.value = true
        return
      }
      
      // 获取所有权限列表
      const permissionsRes = await getAllPermissions()
      const allPermissions = permissionsRes.code === '000000' ? permissionsRes.data || [] : []
      
      // 获取已分配的菜单信息
      const allMenus = flattenMenuTree(JSON.parse(JSON.stringify(originalMenuTreeData.value)))
      const assignedMenus = allMenus.filter(menu => assignedMenuIds.includes(menu.id))
      
      // 构建权限菜单列表
      const allPermissionTypes = ['QUERY', 'ADD', 'EDIT', 'DELETE']
      permissionMenuList.value = assignedMenus.map(menu => {
        // 查找该菜单对应的所有可能的权限（不管是否已分配）
        const menuPermissions = allPermissions.filter(perm => {
          // 根据权限编码匹配菜单编码
          if (perm.permissionCode && menu.menuCode) {
            const permCode = perm.permissionCode.toUpperCase()
            const menuCode = menu.menuCode.toUpperCase()
            return permCode.startsWith(menuCode + '_')
          }
          return false
        })
        
        // 从已分配的权限ID中，找到匹配该菜单的权限
        const assignedMenuPermissions = menuPermissions.filter(perm => 
          assignedPermissionIds.includes(perm.id)
        )
        
        // 提取操作类型（只包含已分配的权限操作类型）
        const menuPermissionIds = assignedMenuPermissions
          .map(perm => {
            const permCode = perm.permissionCode.toUpperCase()
            const menuCode = menu.menuCode.toUpperCase()
            if (permCode.startsWith(menuCode + '_')) {
              const operation = permCode.substring(menuCode.length + 1)
              if (allPermissionTypes.includes(operation)) {
                return operation
              }
            }
            return null
          })
          .filter(op => op !== null)
        
        // 判断该菜单是否有对应的权限定义
        const hasMenuPermissions = menuPermissions.length > 0
        
        // 如果该菜单有对应的权限定义，使用已分配的权限（即使为空数组，表示用户取消了所有权限）
        // 如果该菜单没有对应的权限定义，需要判断是否已经保存过权限
        // 如果 assignedPermissionIds 为空且菜单没有权限定义，说明是第一次打开，默认全部勾选
        // 如果 assignedPermissionIds 不为空，说明已经保存过权限，即使菜单没有权限定义，也应该显示为空（因为用户可能取消了所有权限）
        let permissions = []
        if (hasMenuPermissions) {
          // 该菜单有权限定义，使用已分配的权限（可能为空数组，表示用户取消了所有权限）
          permissions = menuPermissionIds
        } else {
          // 该菜单没有权限定义
          // 如果已经保存过权限（assignedPermissionIds 不为空），说明用户之前可能操作过，应该显示为空
          // 如果还没有保存过权限（assignedPermissionIds 为空），默认全部勾选（第一次打开）
          if (assignedPermissionIds.length > 0) {
            // 已经保存过权限，即使菜单没有权限定义，也应该显示为空（因为用户可能取消了所有权限）
            permissions = []
          } else {
            // 第一次打开，默认全部勾选
            permissions = [...allPermissionTypes]
          }
        }
        
        console.log(`菜单 ${menu.menuName} (${menu.menuCode}):`, {
          hasMenuPermissions,
          menuPermissionsCount: menuPermissions.length,
          assignedMenuPermissionsCount: assignedMenuPermissions.length,
          menuPermissionIds,
          permissions,
          assignedPermissionIdsLength: assignedPermissionIds.length
        })
        
        return {
          menuId: menu.id,
          menuName: menu.menuName,
          menuCode: menu.menuCode,
          permissions: permissions
        }
      })
      
      permissionDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取角色详情失败:', error)
    ElMessage.error('获取角色详情失败')
  }
}

// 权限类型常量
const PERMISSION_TYPES = ['QUERY', 'ADD', 'EDIT', 'DELETE']

/**
 * 判断是否所有权限都被选中
 */
const isAllPermissionsChecked = (row) => {
  if (!row.permissions || row.permissions.length === 0) {
    return false
  }
  return row.permissions.length === PERMISSION_TYPES.length
}

/**
 * 判断是否为半选状态
 */
const isIndeterminate = (row) => {
  if (!row.permissions || row.permissions.length === 0) {
    return false
  }
  return row.permissions.length > 0 && row.permissions.length < PERMISSION_TYPES.length
}

/**
 * 处理全选/取消全选
 */
const handleSelectAll = (row, checked) => {
  if (checked) {
    // 全选：设置所有权限
    row.permissions = [...PERMISSION_TYPES]
  } else {
    // 取消全选：清空所有权限
    row.permissions = []
  }
}

const handleAssignPermissionsSubmit = async () => {
  if (!permissionMenuList.value || permissionMenuList.value.length === 0) {
    ElMessage.warning('请先为角色分配菜单')
    return
  }
  
  try {
    // 获取所有权限列表，用于查找权限ID
    const permissionsRes = await getAllPermissions()
    const allPermissions = permissionsRes.code === '000000' ? permissionsRes.data || [] : []
    
    // 收集所有选中的权限ID
    const selectedPermissionIds = []
    
    // 获取所有菜单信息，用于创建权限时获取菜单名称
    const allMenus = flattenMenuTree(JSON.parse(JSON.stringify(originalMenuTreeData.value)))
    
    for (const menuItem of permissionMenuList.value) {
      // 如果 permissions 为空数组，表示用户取消了该菜单的所有权限，不添加任何权限ID
      // 如果 permissions 有值，只添加选中的权限ID
      if (menuItem.permissions && menuItem.permissions.length > 0) {
        // 获取菜单信息
        const menuInfo = allMenus.find(m => m.id === menuItem.menuId)
        const menuName = menuInfo ? menuInfo.menuName : menuItem.menuName
        
        for (const operation of menuItem.permissions) {
          // 构建权限编码：菜单编码_操作类型
          const permissionCode = `${menuItem.menuCode}_${operation}`
          
          // 查找对应的权限ID
          let permission = allPermissions.find(perm => 
            perm.permissionCode && perm.permissionCode.toUpperCase() === permissionCode.toUpperCase()
          )
          
          // 如果找不到权限，自动创建
          if (!permission) {
            try {
              const operationNames = {
                'QUERY': '查看',
                'ADD': '新增',
                'EDIT': '编辑',
                'DELETE': '删除'
              }
              const operationName = operationNames[operation] || operation
              
              const newPermission = {
                permissionName: `${menuName}-${operationName}`,
                permissionCode: permissionCode,
                permissionType: 'BUTTON',
                description: `${menuName}的${operationName}权限`,
                sortOrder: 0,
                status: 1
              }
              
              console.log('自动创建权限:', newPermission)
              const createRes = await createPermission(newPermission)
              
              if (createRes.code === '000000' && createRes.data) {
                permission = createRes.data
                // 将新创建的权限添加到权限列表中，避免重复创建
                allPermissions.push(permission)
                console.log('权限创建成功:', permission)
              } else {
                console.error('创建权限失败:', createRes)
                ElMessage.warning(`创建权限失败：${menuName}-${operationName}`)
                continue
              }
            } catch (error) {
              console.error('创建权限异常:', error)
              ElMessage.error(`创建权限异常：${menuName}-${operation}，${error.message || '未知错误'}`)
              continue
            }
          }
          
          if (permission) {
            selectedPermissionIds.push(permission.id)
          }
        }
      }
      // 如果 permissions 为空数组或不存在，不添加任何权限ID（表示用户取消了该菜单的所有权限）
    }
    
    console.log('保存的权限ID列表:', selectedPermissionIds)
    
    if (selectedPermissionIds.length === 0) {
      ElMessage.warning('请至少选择一个权限')
      return
    }
    
    const res = await assignPermissions(currentRoleId.value, selectedPermissionIds)
    if (res.code === '000000') {
      ElMessage.success('分配权限成功')
      permissionDialogVisible.value = false
      // 保存成功后，重新获取角色列表、菜单树和权限列表，确保下次打开时能正确显示已保存的权限
      await loadRoleList()
      await loadMenuTree()
      await loadPermissionTree()
    } else {
      ElMessage.error('分配权限失败：' + (res.message || '未知错误'))
    }
  } catch (error) {
    console.error('分配权限失败:', error)
    ElMessage.error('分配权限失败：' + (error.response?.data?.message || error.message || '未知错误'))
  }
}

const handleDialogClose = () => {
  roleFormRef.value?.resetFields()
}

const handleMenuDialogClose = () => {
  // 清理对话框状态，不需要恢复原始菜单树，因为使用的是独立的 dialogMenuTreeData
  checkedMenuIds.value = []
  currentRoleId.value = null
  dialogMenuTreeData.value = []
  if (menuTreeRef.value) {
    menuTreeRef.value.setCheckedKeys([])
  }
}

const handlePermissionDialogClose = () => {
  // 关闭权限分配对话框时清理状态
  permissionMenuList.value = []
  currentRoleId.value = null
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadRoleList()
}

const handlePageChange = (page) => {
  queryParams.pageNum = page
  loadRoleList()
}

// 生命周期
onMounted(() => {
  // 检查是否为系统管理员
  const userInfoStr = localStorage.getItem('userInfo')
  if (userInfoStr) {
    const userInfo = JSON.parse(userInfoStr)
    isSystemAdmin.value = userInfo.userType === 'SYSTEM_ADMIN'
  }
  
  // 加载企业列表（所有用户都需要，因为表单中需要显示）
  loadCompanyList()
  
  loadRoleList()
  loadMenuTree()
  loadPermissionTree()
})
</script>

<style scoped>
.role-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
}

.action-buttons .el-button {
  margin: 0;
  flex-shrink: 0;
  padding: 5px 10px;
}

.action-buttons .el-dropdown {
  margin-left: 0;
}

.menu-node {
  font-weight: bold;
  color: #409eff;
}
</style>

